import { createFileRoute, Link } from "@tanstack/react-router";
import { Activity, BookOpen, Boxes, FlaskConical, Plug, UserCheck } from "lucide-react";

import { seo } from "@/lib/seo";
import { getCaseStudies, getTestimonials } from "@/lib/content";
import {
  ServicePageLayout,
  serviceJsonLd,
  type ServicePageContent,
} from "@/components/service/ServicePageLayout";

const SERVICE_TAG = "ai-implementation";
const PATH = "/services/ai-implementation";
const NAME = "AI Implementation";
const TITLE = "AI Implementation for Small Business";
const SERVICE_TYPE = "AI implementation for small business";
const DESCRIPTION =
  "AI implementation for small businesses that reaches production — integrated with the tools you already run, tested against real work, and documented.";

const FAQS = [
  {
    q: "What does AI implementation involve?",
    a: "AI implementation turns a defined use case into working software or a configured workflow. That usually includes scope, data access, prompts or model selection, integration with existing tools, testing against real cases, human review points, monitoring, and handover. The important part is not the model. It is whether the system works when normal business exceptions appear.",
  },
  {
    q: "How much does AI implementation cost for a small business?",
    a: "We use a fixed-quote model. The price depends on the job, the number of systems involved, the amount of testing needed, and the cost of being wrong. You get the scope and quote before work starts. We do not ask you to approve an open-ended hourly build.",
  },
  {
    q: "Will it work with our accounting system, CRM, or Microsoft 365?",
    a: "Integration with existing tools is the default. We routinely plan around accounting systems, CRMs, email, calendars, document storage, forms, and reporting tools. The exact connection method depends on what your accounts allow and whether the tool has a reliable way to exchange data.",
  },
  {
    q: "What happens if the AI gets something wrong?",
    a: "We design for that. Some outputs should be reviewed by a person before they reach a customer. Some need confidence checks, exception queues, or a fallback path. The review point depends on the risk of the task, not on a generic rule that AI should always be trusted or never be trusted.",
  },
  {
    q: "Do we need a developer on staff afterwards?",
    a: "Not always. Many small business builds can be operated by a trained owner inside the team, with documentation and a support plan. If the system requires technical maintenance, we say that before you approve the build so you can decide whether it belongs in your business.",
  },
  {
    q: "Do you work with our existing systems?",
    a: "Yes. Replacing working software is expensive and rarely necessary. Most of what we build sits beside the current stack, reads from it, writes back to it when appropriate, and gives people a better way to do the job they already have.",
  },
  {
    q: "Who owns the code and configuration?",
    a: "You do. Source, configuration, documentation, and account access are handed over in the way agreed in the scope. We retain our general methods and know-how, not your system or your data.",
  },
  {
    q: "Which AI models do you use?",
    a: "Whichever fits the job, the budget, and your data constraints. We are not resellers for a specific model provider. When a rules-based workflow is safer or cheaper than AI, we recommend that instead.",
  },
  {
    q: "How do you test an AI implementation?",
    a: "We build an evaluation set from real cases where the right answer is known or can be judged. That lets us compare outputs, catch regressions, and avoid testing only on clean examples that never resemble actual work.",
  },
  {
    q: "Can you take over a prototype someone else built?",
    a: "Often, yes. We first inspect what exists, what it connects to, how it fails, and whether anyone is using it. If the prototype is the wrong shape for production, we will say so before quoting the next step.",
  },
] as const;

const CONTENT: ServicePageContent = {
  path: PATH,
  name: NAME,
  eyebrow: "AI Implementation",
  h1: "AI implementation that reaches production, not just a demo.",
  lead: "We build the opportunity you decided to pursue — integrated with the systems you already run, tested against real work rather than happy-path examples, and handed over with documentation your team can maintain.",
  problem: {
    heading: "A prototype is not a system.",
    paragraphs: [
      "Getting an AI feature to work once, on a clean example, in a notebook, is the easy part. The hard part is everything after: the edge cases, the integration with the software your team already lives in, the moment the model returns something confidently wrong, and the question of who fixes it when the customer is waiting.",
      "Most stalled AI projects are stuck exactly there. The demo worked, and then nobody could make it survive contact with real operations. It did not have an owner, a review step, a test set, a recovery path, or a clear answer to what happens when the input is messy.",
      "Our implementation work starts with the operating job, not the model. We define the task, place the human review where it belongs, connect the surrounding tools, and make sure the system has enough logging and documentation to be owned after launch.",
    ],
  },
  useCases: {
    heading: "What AI implementation for small business can look like.",
    intro:
      "The right build is usually close to work your team already does. These examples show the kind of production systems we scope after the use case is clear.",
    groups: [
      {
        area: "Customer communications",
        examples: [
          "A drafting assistant inside the existing inbox that proposes replies for a human to review and send.",
          "A triage workflow that reads incoming requests, assigns a category, and routes exceptions to the right person.",
          "A customer-summary view that pulls history, open issues, and next steps into one place before a call.",
          "A response library that learns from approved language rather than random internet examples.",
        ],
      },
      {
        area: "Documents and data",
        examples: [
          "Document extraction that fills the fields your team currently re-types.",
          "A review queue for contracts, applications, or forms that flags missing information before submission.",
          "A data-cleanup assistant that standardises labels and catches obvious inconsistencies.",
          "A searchable document layer that answers from approved internal material.",
        ],
      },
      {
        area: "Sales and proposals",
        examples: [
          "A proposal first-draft generator that works from your past proposals and current scope notes.",
          "Lead qualification summaries compiled from forms, emails, and call notes.",
          "A follow-up assistant that drafts next-step messages for review after sales conversations.",
          "A quote support tool that checks the inputs needed before pricing can be approved.",
        ],
      },
      {
        area: "Scheduling",
        examples: [
          "An intake-to-calendar workflow that gathers constraints before a person confirms the appointment.",
          "A rescheduling assistant that proposes available slots and keeps a human in charge of edge cases.",
          "A dispatch support view that explains why a job should go to a specific team member.",
          "Reminder messages drafted from the appointment type and customer context.",
        ],
      },
      {
        area: "Knowledge",
        examples: [
          "A staff answer tool grounded in policies, procedures, and approved training material.",
          "A new-hire support assistant that points to the right internal document and explains the answer.",
          "A manager-facing knowledge base that turns recurring questions into maintained guidance.",
          "A search layer that cites the source so people can check the answer.",
        ],
      },
      {
        area: "Finance and admin",
        examples: [
          "Invoice intake that reads attachments, extracts fields, and prepares them for review.",
          "An approval workflow that checks whether the right documentation is attached before routing.",
          "Monthly reporting support that gathers exports and drafts the narrative for a manager to edit.",
          "Admin task queues that separate routine items from exceptions needing judgment.",
        ],
      },
    ],
  },
  cards: [
    {
      icon: Boxes,
      title: "Working software",
      body: "Deployed and running against real work, not a proof of concept.",
    },
    {
      icon: Plug,
      title: "Integration with your stack",
      body: "It works inside the tools your team already uses, rather than adding another tab.",
    },
    {
      icon: FlaskConical,
      title: "An evaluation set",
      body: "Real cases with known-good answers, so you can tell whether a change made it better or worse.",
    },
    {
      icon: UserCheck,
      title: "Human-in-the-loop design",
      body: "Clear points where a person reviews, corrects, or overrides — sized to the cost of being wrong.",
    },
    {
      icon: Activity,
      title: "Monitoring",
      body: "Visibility into what it is doing and alerting when behaviour drifts.",
    },
    {
      icon: BookOpen,
      title: "Documentation and handover",
      body: "Written so your team can maintain it without us.",
    },
  ],
  deliverables: {
    heading: "What you actually receive.",
    intro: "You own it. No lock-in to us.",
    items: [
      "The deployed system, running in your environment",
      "Source code and configuration, with your team holding the keys",
      "An evaluation set of real cases with expected outputs",
      "A runbook covering failure modes and what to do about them",
      "A handover session with the people who will operate it",
    ],
  },
  readiness: {
    heading: "When implementation is the right next move.",
    ready: [
      "You know the specific job the system should do, not just that you want to use AI somewhere.",
      "The people who will use it daily can describe the current process and its exceptions.",
      "There is a clear owner who will approve decisions and receive the handover.",
      "The surrounding tools have a practical way to exchange data or support the workflow.",
      "You are willing to keep human review where the cost of being wrong is high.",
    ],
    notYet: [
      "The use case is still only “use AI somewhere.”",
      "The process changes every week and nobody can say what the stable version is.",
      "The team that would use it does not agree that the problem matters.",
      "No one can provide realistic examples to test against.",
      "The goal is to remove judgment from a task that still clearly needs it.",
    ],
  },
  process: {
    heading: "How a build runs.",
    steps: [
      {
        n: "1",
        title: "Scope and design",
        body: "We agree exactly what it does, what it does not, and how we will know it works — in writing, before building.",
      },
      {
        n: "2",
        title: "Build",
        body: "Working software in short increments, visible to you throughout. No reveal at the end.",
      },
      {
        n: "3",
        title: "Evaluate",
        body: "Tested against real cases from your business, including the awkward ones.",
      },
      {
        n: "4",
        title: "Deploy and monitor",
        body: "Into production with monitoring in place, and a period of watching it closely.",
      },
      {
        n: "5",
        title: "Handover",
        body: "Documentation, training, and a clear answer to who maintains it.",
      },
    ],
  },
  timeline: {
    heading: "How long it takes.",
    body: "Implementation time depends on the task, the systems involved, the review rules, and how much testing the risk requires. A small internal assistant may be measured in days or a few weeks. A workflow that touches customer communication, financial records, or several systems needs more care. We do not set a date until the written scope is clear.",
  },
  pricing: {
    heading: "What it costs.",
    body: "Builds are fixed-quote against a written scope agreed before work begins. Scope changes are re-quoted rather than absorbed silently.",
  },
  related: {
    heading: "What comes before a build, and what sits next to it.",
    body: (
      <>
        <p>
          Not sure this is the right starting point? The{" "}
          <Link to="/tools/ai-opportunity-scorecard">free AI readiness scorecard</Link> takes a few
          minutes, and an{" "}
          <Link to="/services/ai-opportunity-assessment">AI opportunity assessment</Link> is the
          longer version of the same question: what is worth building first.
        </p>
        <p>
          Plenty of builds turn out to be mostly plumbing rather than models. When that is the case
          we say so and scope it as{" "}
          <Link to="/services/workflow-automation">AI workflow automation</Link> instead.
        </p>
      </>
    ),
  },
  faqs: FAQS,
  cta: {
    heading: "Have something you already want built?",
    line: "Bring the use case. We will tell you on the first call whether it is ready for a build or needs more definition.",
  },
};

export const Route = createFileRoute("/_site/services/ai-implementation")({
  head: () =>
    seo({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      jsonLd: serviceJsonLd({
        path: PATH,
        name: NAME,
        description: DESCRIPTION,
        serviceType: SERVICE_TYPE,
        faqs: FAQS,
      }),
    }),
  loader: async () => {
    const [caseStudies, testimonials] = await Promise.all([
      getCaseStudies({ data: {} }),
      getTestimonials({ data: { limit: 3 } }),
    ]);
    return {
      caseStudies: caseStudies.filter((study) => study.services?.includes(SERVICE_TAG)).slice(0, 3),
      testimonials: testimonials.filter((t) => !t.service_tag || t.service_tag === SERVICE_TAG),
    };
  },
  component: Page,
  errorComponent: () => <ServicePageLayout content={CONTENT} />,
  notFoundComponent: () => null,
});

function Page() {
  const { caseStudies, testimonials } = Route.useLoaderData();
  return (
    <ServicePageLayout content={CONTENT} caseStudies={caseStudies} testimonials={testimonials} />
  );
}
